import liveServerStatus from '../../liveServerStatus';

/**
 * Generate HTML email template for profile update notifications
 * @param {string} firstName - User's first name
 * @param {string} updateType - Type of update (personal_info, bank_details, emergency_contact, declaration, documents)
 * @param {Object} updatedFields - Fields that were updated
 * @param {string} userEmail - User's email address
 * @returns {string} - HTML email content
 */
export const generateProfileUpdateEmailHTML = (
  firstName,
  updateType,
  updatedFields,
  userEmail
) => {
  // Define update type details
  const updateTypeDetails = {
    personal_info: {
      title: 'Personal Information Updated',
      icon: '👤',
      description: 'Your personal information has been successfully updated in the MyelinZ PPC Portal.',
      color: '#2196f3'
    },
    bank_details: {
      title: 'Bank Details Updated',
      icon: '🏦',
      description: 'Your banking information has been successfully updated in the MyelinZ PPC Portal.',
      color: '#4caf50'
    },
    emergency_contact: {
      title: 'Emergency Contact Updated',
      icon: '🚨',
      description: 'Your emergency contact information has been successfully updated in the MyelinZ PPC Portal.',
      color: '#ff9800'
    },
    declaration: {
      title: 'Declaration Details Updated',
      icon: '📋',
      description: 'Your declaration details have been successfully updated in the MyelinZ PPC Portal.',
      color: '#9c27b0'
    },
    documents: {
      title: 'Profile Documents Updated',
      icon: '📄',
      description: 'Your profile documents have been successfully updated in the MyelinZ PPC Portal.',
      color: '#607d8b'
    }
  };

  const details = updateTypeDetails[updateType] || updateTypeDetails.personal_info;
  
  // Format updated fields for display
  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .replace(/Dob/g, 'Date of Birth')
      .replace(/Id/g, 'ID');
  };

  const updatedFieldsList = Object.keys(updatedFields || {}).length > 0 
    ? Object.keys(updatedFields).map(field => 
        `<li style="margin-bottom: 8px; color: #424242;">
          <strong>${formatFieldName(field)}:</strong> Updated
        </li>`
      ).join('')
    : '<li style="color: #424242;">Profile information updated</li>';

  // Add test environment indicator in the header if not in live environment
  const testEnvironmentBanner = !liveServerStatus ? `
    <div style="background-color: #ffeb3b; color: #333; text-align: center; padding: 5px; font-weight: bold; font-size: 12px;">
      ⚠️ TEST EMAIL - No action required ⚠️
    </div>
  ` : '';

  // Format current date and time
  const updateDateTime = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${details.title}</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
          background-color: #f5f5f5;
        }
        .email-container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
        }
        .email-header {
          background-color: #424242;
          color: white;
          padding: 20px;
          text-align: center;
        }
        .email-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
        }
        .update-icon {
          font-size: 32px;
          margin-bottom: 10px;
          display: block;
        }
        .email-body {
          padding: 30px;
        }
        .greeting {
          font-size: 18px;
          font-weight: 500;
          margin-bottom: 20px;
          color: #212121;
        }
        .update-summary {
          background-color: #f8f9fa;
          border-left: 4px solid ${details.color};
          border-radius: 6px;
          padding: 20px;
          margin-bottom: 25px;
        }
        .update-title {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 10px;
          color: #212121;
          display: flex;
          align-items: center;
        }
        .update-description {
          color: #424242;
          margin-bottom: 15px;
        }
        .updated-fields {
          margin-top: 15px;
        }
        .updated-fields h4 {
          margin: 0 0 10px 0;
          color: #212121;
          font-size: 14px;
          font-weight: 600;
        }
        .updated-fields ul {
          margin: 0;
          padding-left: 20px;
        }
        .update-info {
          background-color: #f5f5f5;
          border-radius: 6px;
          padding: 15px;
          margin-bottom: 25px;
        }
        .update-info-item {
          margin-bottom: 8px;
        }
        .update-info-label {
          font-size: 12px;
          text-transform: uppercase;
          color: #757575;
          margin-bottom: 2px;
        }
        .update-info-value {
          font-weight: 500;
          color: #212121;
        }
        .divider {
          height: 1px;
          background-color: #e0e0e0;
          margin: 25px 0;
        }
        .cta-button {
          display: inline-block;
          background-color: ${details.color};
          color: white;
          text-decoration: none;
          padding: 12px 25px;
          border-radius: 4px;
          font-weight: 500;
          margin-top: 15px;
          text-align: center;
        }
        .message {
          margin-bottom: 20px;
          color: #424242;
        }
        .security-note {
          background-color: #fff3cd;
          border: 1px solid #ffeaa7;
          border-radius: 4px;
          padding: 15px;
          margin-bottom: 25px;
        }
        .security-note h4 {
          margin: 0 0 8px 0;
          color: #856404;
          font-size: 14px;
        }
        .security-note p {
          margin: 0;
          color: #856404;
          font-size: 13px;
        }
        .email-footer {
          background-color: #212121;
          padding: 20px;
          text-align: center;
          color: #f5f5f5;
          font-size: 12px;
        }
        .company-name {
          font-weight: 600;
          color: #f5f5f5;
          margin-bottom: 8px;
        }
      </style>
    </head>
    <body>
      ${testEnvironmentBanner}
      <div class="email-container">
        <div class="email-header">
          <span class="update-icon">${details.icon}</span>
          <h1>${details.title}</h1>
        </div>
        
        <div class="email-body">
          <div class="greeting">
            Hello ${firstName || 'there'},
          </div>
          
          <div class="update-summary">
            <div class="update-title">
              ${details.title}
            </div>
            <div class="update-description">
              ${details.description}
            </div>
            
            <div class="updated-fields">
              <h4>Updated Information:</h4>
              <ul>
                ${updatedFieldsList}
              </ul>
            </div>
          </div>
          
          <div class="update-info">
            <div class="update-info-item">
              <div class="update-info-label">Updated On</div>
              <div class="update-info-value">${updateDateTime}</div>
            </div>
            <div class="update-info-item">
              <div class="update-info-label">Account</div>
              <div class="update-info-value">${userEmail}</div>
            </div>
          </div>
          
          <a href="${process.env.REACT_APP_URL || 'https://revamped-ppc.myelinz.com'}/profile" class="cta-button">
            View Profile
          </a>
          
          <div class="divider"></div>
          
          <div class="security-note">
            <h4>🔐 Security Notice</h4>
            <p>
              If you did not make this change or if you believe this update was made in error, 
              please contact the HR team immediately at hr@myelinz.com or through the portal.
            </p>
          </div>
          
          <div class="message">
            This confirmation email helps keep your account secure. Your updated information is now active in the MyelinZ PPC Portal.
          </div>
        </div>
        
        <div class="email-footer">
          <div class="company-name">MyelinZ PPC Portal</div>
          <p>This is an automated message. Please do not reply directly to this email.</p>
          <p>© ${new Date().getFullYear()} MyelinZ. All rights reserved.</p>
          ${!liveServerStatus ? '<p><strong>TEST ENVIRONMENT - No action required</strong></p>' : ''}
        </div>
      </div>
    </body>
    </html>
  `;
};

export default generateProfileUpdateEmailHTML;
